import { Avatar, Box } from "@mui/material";
import CustomDataGridV2 from "../../../../../../components/CustomDataGridV2";
import Text from "../../../../../../components/Text";
import { applyDefaultColumnSettings } from "../../../../../../utils/utils";

// Top summary columns for patient listing
const PATIENT_LISTING_TOP_COLUMNS = [
  { field: "id", headerName: "Patient ID", minWidth: 100, flex: 1 },
  {
    field: "patientName",
    headerName: "Patient Name",
    minWidth: 200,
    flex: 1.5,
    renderCell: ({ row }: { row: any }) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar src={row.avatar} sx={{ width: 32, height: 32 }} />
        <Box>
          <Text color="primary.main" fontWeight={600} fontSize={15}>
            {row.patientName}
          </Text>
          <Text fontSize={13} color="grey.700">
            {row.ageGender}
          </Text>
        </Box>
      </Box>
    ),
  },
  {
    field: "days_0_30",
    headerName: "0-30 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_31_60",
    headerName: "31-60 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_61_90",
    headerName: "61-90 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_91_120",
    headerName: "91-120 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_120_plus",
    headerName: "120+ Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "total",
    headerName: "Total",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
];

const PATIENT_LISTING_TOP_ROW = [
  {
    id: "A8431",
    patientName: "Henna West",
    avatar: "/avatars/henna.png",
    ageGender: "65 yrs-female",
    days_0_30: 10,
    days_31_60: 20,
    days_61_90: 20,
    days_91_120: 10,
    days_120_plus: 53,
    total: 95,
  },
];

// Main table columns for patient listing
const PATIENT_LISTING_DETAIL_COLUMNS = applyDefaultColumnSettings([
  { field: "superbillId", headerName: "Superbill Id", minWidth: 120, flex: 1 },
  {
    field: "dateOfService",
    headerName: "Date of Service",
    minWidth: 140,
    flex: 1,
  },
  {
    field: "dateOfBilling",
    headerName: "Date of Billing",
    minWidth: 140,
    flex: 1,
  },
  {
    field: "billAmount",
    headerName: "Bill Amount",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "claimAmount",
    headerName: "Claim Amount",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "claimPaid",
    headerName: "Claim Paid",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "coPay",
    headerName: "Co-Pay",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "coPayPaid",
    headerName: "Co-Pay Paid",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "balanceAmount",
    headerName: "Balance Amount",
    minWidth: 140,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
]);

const PATIENT_LISTING_DETAIL_DUMMY_DATA = [
  {
    id: 1,
    superbillId: "34576",
    dateOfService: "Dec 10 2021",
    dateOfBilling: "Dec 15 2021",
    billAmount: 120,
    claimAmount: 100,
    claimPaid: 0,
    coPay: 20,
    coPayPaid: 10,
    balanceAmount: 110,
  },
  {
    id: 2,
    superbillId: "27478",
    dateOfService: "Nov 15 2021",
    dateOfBilling: "Nov 18 2021",
    billAmount: 200,
    claimAmount: 140,
    claimPaid: 140,
    coPay: 60,
    coPayPaid: 30,
    balanceAmount: 30,
  },
  {
    id: 3,
    superbillId: "16532",
    dateOfService: "Oct 01 2021",
    dateOfBilling: "Oct 06 2021",
    billAmount: 250,
    claimAmount: 200,
    claimPaid: 200,
    coPay: 40,
    coPayPaid: 10,
    balanceAmount: 30,
  },
  {
    id: 4,
    superbillId: "68786",
    dateOfService: "Sep 23 2021",
    dateOfBilling: "Sep 26 2021",
    billAmount: 140,
    claimAmount: 90,
    claimPaid: 90,
    coPay: 30,
    coPayPaid: 10,
    balanceAmount: 10,
  },
  {
    id: 5,
    superbillId: "37417",
    dateOfService: "Aug 29 2021",
    dateOfBilling: "Aug 31 2021",
    billAmount: 80,
    claimAmount: 40,
    claimPaid: 40,
    coPay: 20,
    coPayPaid: 10,
    balanceAmount: 10,
  },
];

const PatientListingTable = () => {
  const topColumnHeight = 130;
  return (
    <Box>
      <Box height={`${topColumnHeight}px`}>
        <CustomDataGridV2
          rows={PATIENT_LISTING_TOP_ROW}
          hideFooter
          rowCount={PATIENT_LISTING_TOP_ROW.length}
          getRowId={(row) => row.id}
          rowHeight={40}
          columns={PATIENT_LISTING_TOP_COLUMNS}
        />
      </Box>
      <Box height={`calc(100vh - ${topColumnHeight + 168}px)`}>
        <CustomDataGridV2
          rows={PATIENT_LISTING_DETAIL_DUMMY_DATA}
          columns={PATIENT_LISTING_DETAIL_COLUMNS}
          getRowId={(row) => row.id}
          rowHeight={60}
        />
      </Box>
    </Box>
  );
};

export default PatientListingTable;
